/**
 * Org-scope context — resolves the organization id the current admin is acting
 * within, which every org-scoped API call passes as `orgId`.
 *
 * Mirrors the backend `resolveOrgScope`:
 *  - ORG_ADMIN is pinned to `principal.orgId` (selection is a no-op).
 *  - META_ADMIN has no home org and must choose one; the choice is persisted in
 *    sessionStorage so navigating between ticket/cluster/config views keeps it.
 *
 * The resolved scope is exposed both as the raw `orgId` (string | undefined) and
 * as a ready-to-spread `{ orgId }` for client-core methods.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useAuth } from '../auth/AuthContext.js';

const SCOPE_KEY = 'hotline.activeOrgId';

export interface OrgScopeValue {
  /** The effective org id, or undefined if a meta-admin hasn't picked one. */
  orgId: string | undefined;
  /** True when the caller is a meta-admin (must explicitly choose an org). */
  isMeta: boolean;
  /** Set the active org (meta-admin only; ignored for org-admin). */
  setOrgId: (id: string | undefined) => void;
  /** Spread into client-core admin calls: `api.listTickets(q, scope.query)`. */
  query: { orgId?: string };
}

const OrgScopeContext = createContext<OrgScopeValue | null>(null);

function readPersisted(): string | undefined {
  try {
    return globalThis.sessionStorage?.getItem(SCOPE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function OrgScopeProvider({ children }: { children: ReactNode }): ReactElement {
  const { principal, isMetaAdmin } = useAuth();
  const [metaOrgId, setMetaOrgId] = useState<string | undefined>(readPersisted);

  const setOrgId = useCallback((id: string | undefined) => {
    setMetaOrgId(id);
    try {
      if (id) globalThis.sessionStorage?.setItem(SCOPE_KEY, id);
      else globalThis.sessionStorage?.removeItem(SCOPE_KEY);
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const orgId = isMetaAdmin ? metaOrgId : (principal?.orgId ?? undefined);

  const value = useMemo<OrgScopeValue>(
    () => ({
      orgId,
      isMeta: isMetaAdmin,
      setOrgId,
      query: { orgId },
    }),
    [orgId, isMetaAdmin, setOrgId],
  );

  return (
    <OrgScopeContext.Provider value={value}>{children}</OrgScopeContext.Provider>
  );
}

export function useOrgScope(): OrgScopeValue {
  const ctx = useContext(OrgScopeContext);
  if (!ctx) {
    throw new Error('useOrgScope must be used within an <OrgScopeProvider>');
  }
  return ctx;
}
